/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { Component, Input, OnInit } from '@angular/core';
import { WizardService } from '@core/services/wizard/wizard.service';
import { takeUntil } from 'rxjs/operators';
import { StepItemModel } from './models/index';
import { DestroyDirective } from '../destroy-abstract/destroy.abstract';
import { WizardStepDTO } from '@api/models/';
import { AppService } from '@core/services/app/app.service';

@Component({
  selector: 'app-wizard-widget',
  templateUrl: './wizard-widget.component.html',
  styleUrls: ['./wizard-widget.component.scss'],
  standalone: false
})
export class WizardWidgetComponent extends DestroyDirective implements OnInit {
  /**
   * se true visualizza solo i raggruppamenti degli step e non la lista completa di step
   */
  @Input() grouped = false;

  /**
   * lista di step che compongono il wizard
   */
  stepList: StepItemModel[] = [];

  /**
   * step aperto
   */
  currentStep: string;

  /**
   * numero di step totale
   */
  allStepsCount: number;

  /**
   * numero di step principali
   */
  mainStepsCount: number;

  constructor(
    private wizardService: WizardService,
    private appService: AppService
  ) {
    super();
  }

  ngOnInit() {
    this.watchWizard();
  }

  /**
   * osserva gli update del wizard
   */
  private watchWizard(): void {
    this.wizardService
      .getWizardWhenChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setCurrentStep();
        this.setStepList();
        this.countSteps();
      });
  }

  /**
   * imposta la lista di tutti gli step
   */
  private setStepList(): void {
    this.stepList = [];
    let prevGroupCod: string;

    this.getWizardVisibleSteps().forEach(step => {
      const stepGroupCod = step.groupCod;

      if (!!stepGroupCod && stepGroupCod !== prevGroupCod) {
        this.addGroupStep(step);

        prevGroupCod = stepGroupCod;
      }

      this.addFlowStep(step);
    });
  }

  private addGroupStep(step: WizardStepDTO): void {
    this.addStepItem({
      ...step,
      stepCode: `group-${step.groupCod}`,
      completed: this.isGroupCompleted(step.groupCod),
      active: this.isGroupActive(step.groupCod),
      child: false
    });
  }

  private addFlowStep(step: WizardStepDTO): void {
    this.addStepItem({
      ...step,
      completed: step.status === 2,
      active: step.stepCode === this.currentStep,
      child: !!step.groupCod
    });
  }

  private addStepItem(item: StepItemModel): void {
    if (item.child && this.grouped) {
      return;
    }

    this.stepList.push(item);
  }

  private isGroupCompleted(groupCod: string): boolean {
    return (
      !!groupCod &&
      this.getWizardVisibleSteps().every(
        x => x.groupCod === groupCod && x.status === 2
      )
    );
  }

  private isGroupActive(groupCod: string): boolean {
    return !!this.getWizardVisibleSteps().find(
      x => x.groupCod === groupCod && x.stepCode === this.currentStep
    );
  }

  private getWizardVisibleSteps(): WizardStepDTO[] {
    return this.wizardService
      .getWizardSteps()
      .filter(step => !this.isStepToHide(step));
  }

  /**
   * stabilisce se lo step non è da visualizzare all'utente
   */
  private isStepToHide(step: WizardStepDTO): boolean {
    const stepsToHide = (
      this.appService.getConfig().wizard.hiddenRecapStepsCode || []
    ).map(x => x.toUpperCase());

    if (!step?.stepCode) {
      return true;
    }

    const stepCode = step.stepCode.toUpperCase();
    return stepCode.includes('WAIT') || stepsToHide.includes(stepCode);
  }

  /**
   * imposta il codice dello step aperto
   */
  private setCurrentStep(): void {
    const currentStepIndex = this.wizardService.findFirstOpenStepIndex();
    const step = this.wizardService.getWizardSteps()[currentStepIndex];

    this.currentStep = !!step ? step.stepCode : null;
  }

  /**
   * imposta il numero totale di step parent
   */
  private countSteps(): void {
    this.allStepsCount = this.stepList.length;
    this.mainStepsCount = this.stepList.filter(x => !x.child).length;
  }
}
